import { useRef, useState } from 'react';
import type { Pilot } from '@edh/shared';
import { Avatar } from './Avatar';
import { Sheet } from './Sheet';
import { updateSelf } from '../lib/api';
import { AvatarError, fileToAvatar } from '../lib/avatar';

/**
 * Your own name and picture. Only ever your own — the endpoint behind this
 * takes the member to edit from the verified token, not from anything this
 * form sends, so there is nothing here that could be pointed at somebody else.
 *
 * Ratings are absent on purpose. They are earned, not set.
 */
export function EditProfile({
  me,
  onClose,
  onSaved,
}: {
  me: Pilot;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(me.name);
  const [avatar, setAvatar] = useState<string | null>(me.avatar);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const dirty = name.trim() !== me.name || avatar !== me.avatar;

  async function pickImage(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      setAvatar(await fileToAvatar(file));
    } catch (err) {
      setError(err instanceof AvatarError ? err.message : 'could not use that image');
    }
  }

  async function save() {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await updateSelf({ name: name.trim(), avatar });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not save that');
      setBusy(false);
    }
  }

  return (
    <Sheet title="Your profile" onClose={onClose}>
      <label className="field">
        <span>Name</span>
        <input
          className="input"
          value={name}
          maxLength={40}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && dirty) void save();
          }}
        />
      </label>

      <div className="field">
        <span>Picture (optional)</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar name={name || me.name} src={avatar} size={64} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button
              className="btn ghost"
              style={{ padding: '7px 14px', fontSize: 14 }}
              onClick={() => fileInput.current?.click()}
            >
              {avatar ? 'Change picture' : 'Add a picture'}
            </button>
            {avatar && (
              <button
                className="btn ghost"
                style={{ padding: '7px 14px', fontSize: 13, color: 'var(--muted)' }}
                onClick={() => setAvatar(null)}
              >
                Remove
              </button>
            )}
          </div>
        </div>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => void pickImage(e.target.files?.[0])}
        />
      </div>

      {me.email && (
        <p className="hint" style={{ marginTop: -6, marginBottom: 14 }}>
          Signed in as {me.email}. That comes from your Google account and cannot be changed
          here.
        </p>
      )}

      {error && <p className="error">{error}</p>}

      <div className="btn-row">
        <button className="btn ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={!dirty || !name.trim() || busy} onClick={() => void save()}>
          {busy ? <span className="spinner" /> : 'Save'}
        </button>
      </div>
    </Sheet>
  );
}
